import React from 'react';
import _ from 'lodash';
import bemify, { toModifiers } from 'bemify-js';

import Icon from 'app/common/components/Icon';
import { Div } from 'app/common/components/AccessibleElement';
import 'app/common/components/ToggleSwitch.scss';

import type { KindType } from 'app/common/types';

const bem = bemify('toggle-switch');

const getAriaLabel = ({
  isToggling,
  isOn,
  noun,
}: {
  isToggling?: boolean;
  isOn: boolean;
  noun: string;
}) => {
  const onOrOff = isOn ? 'on' : 'off';
  return isToggling ? `turning ${onOrOff}` : `${noun} is ${onOrOff}`;
};

interface ToggleSwitchProps {
  icon?: boolean;
  isDisabled?: boolean;
  isOn: boolean;
  isToggling?: boolean;
  kind?: KindType;
  onToggle: (isOn: boolean, event: React.SyntheticEvent<HTMLElement>) => void;
  className?: string;
  noun?: string;
  title?: string;
}

const ToggleSwitch = ({
  isOn,
  isDisabled = false,
  isToggling = false,
  kind = '',
  icon = false,
  onToggle,
  className = '',
  noun = 'toggle',
  title = 'toggle',
}: ToggleSwitchProps) => {
  const onClick = (event: React.SyntheticEvent<HTMLElement>) => {
    event.preventDefault();
    onToggle(!isOn, event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      onClick(event);
    }
  };

  return (
    <Div
      aria-disabled={isDisabled}
      aria-label={getAriaLabel({ isOn, isToggling, noun })}
      checked={isOn}
      className={bem(className, toModifiers(kind, { isDisabled }))}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="checkbox"
      tabIndex={0}
      title={title}
    >
      <input
        aria-checked={isOn}
        aria-disabled={isDisabled}
        aria-label="onoffswitch"
        checked={!!isOn}
        className={bem('__toggle-checkbox', toModifiers(kind))}
        name="onoffswitch"
        onChange={_.noop}
        // required if 'checked' is initially on.
        type="checkbox"
        value={isOn ? 'on' : 'off'}
      />{' '}
      <span
        className={bem('__text-and-background', toModifiers(kind, { isOn }))}
        key="text-and-background"
      />
      {icon ? (
        <Icon
          className={bem(kind, '__icon', toModifiers({ isOn, isToggling }))}
          type="eye"
        />
      ) : (
        <span
          className={bem(
            '__toggle-dot',
            toModifiers(kind, { isOn, isToggling })
          )}
          key="toggle-dot"
        />
      )}
    </Div>
  );
};

// Use React.memo for performance (equivalent to pure from recompose)
export default React.memo(ToggleSwitch);
